import {
  GridExcelExportOptions,
  GridRowId,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { jsPDF } from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { last, sortBy } from 'lodash';
import moment from 'moment/moment';
import { MutableRefObject } from 'react';

type PDFDocumentDetails = {
  title: string;
  subtitle?: string;
};

export const exportToPDF =
  (apiRef: MutableRefObject<GridApiPremium>) =>
  (options: GridExcelExportOptions, documentDetails: PDFDocumentDetails) => {
    const { fileName, getRowsToExport } = options;

    const pageMargins = {
      vertical: 50,
      horizontal: 50,
    };
    const timestamp = {
      fontSize: 10,
    };

    // Solaforce Logo
    const img = new Image();
    img.src = '/v2/img/logoBlueFooter.png';

    const imgSize = {
      width: 116,
      height: 24,
    };

    const resolveLogoPosition = (
      pageWidth: number,
      width: number,
      height: number,
    ) => {
      return {
        x: pageWidth - width - pageMargins.horizontal,
        y: pageMargins.vertical - height,
        w: width,
        h: height,
      };
    };

    // Document Details
    const resolveDocumentDetails = () => {
      const { title, subtitle } = documentDetails;

      const documentDetailRows = [
        {
          index: 1,
          text: title,
          fontSize: 20,
        },
        {
          index: 2,
          text: subtitle ?? '',
          fontSize: 15,
          color: [128, 128, 128],
        },
      ]
        .filter(({ text }) => {
          return !!text && text.length > 0;
        })
        .map((row, index, array) => {
          const previousRowHeights = array.slice(0, index);

          return {
            ...row,
            rowOffset: previousRowHeights.reduce(
              (total, { fontSize }) => total + fontSize * 1.3,
              0,
            ),
          };
        });

      const documentHeadBuffer = 15;
      const lastOffset = last(documentDetailRows)?.rowOffset;
      const detailsHeight = !!lastOffset ? lastOffset + documentHeadBuffer : 0;

      const logoStampHeight =
        imgSize.height + timestamp.fontSize + documentHeadBuffer;

      return {
        rows: documentDetailRows,
        height:
          detailsHeight > logoStampHeight ? detailsHeight : logoStampHeight,
      };
    };

    const documentDetailsSection = resolveDocumentDetails();

    // Table Columns
    const visibleColumns = (apiRef.current.getVisibleColumns() ?? []).filter(
      (column) => !column.disableExport,
    );
    const columns = visibleColumns.map((column) => {
      const { field, headerName } = column;
      return { dataKey: field, header: headerName ?? field };
    });
    const columnStyles = Object.fromEntries(
      visibleColumns.map((column) => {
        const { field, minWidth } = column;

        return [
          field,
          {
            minCellWidth: minWidth,
          },
        ];
      }),
    );

    // Table Column Groups
    const getTableColumnGroups = (): RowInput[] => {
      const getGroupPath = (field: string) =>
        apiRef.current.unstable_getColumnGroupPath(field);
      const allGroupDetails =
        apiRef.current.unstable_getAllGroupDetails() ?? {};

      const getColumnGroupHeaderName = (groupId: string) => {
        const group = allGroupDetails[groupId];

        return !!group ? group.headerName ?? group.groupId : '';
      };

      const fieldGroups = visibleColumns.map(({ field }) => {
        return {
          field,
          groupIds: getGroupPath(field),
        };
      });

      const groupLevelsCount = fieldGroups.reduce((acc, { groupIds }) => {
        return groupIds.length > acc ? groupIds.length : acc;
      }, 0);

      if (groupLevelsCount < 1) {
        return [];
      }

      const groupLevelIndexes = Array(groupLevelsCount)
        .fill(' ')
        .map((_value, index) => index);

      const filledFieldGroups = fieldGroups.map((fieldInfo) => {
        const { groupIds } = fieldInfo;

        if (groupIds.length < groupLevelsCount) {
          return {
            ...fieldInfo,
            groupIds: groupIds.concat(
              Array(groupLevelsCount - groupIds.length).fill(' '),
            ),
          };
        }

        return fieldInfo;
      });

      const getLevelGroupIds = (levelIndex: number): string[] => {
        return filledFieldGroups.map(({ groupIds }) => groupIds[levelIndex]);
      };

      const getColSpans = (groupIds: string[]) => {
        const result = [];
        let counter = 1;
        for (let i = 0; i < groupIds.length; i++) {
          if (groupIds[i] === groupIds[i + 1]) {
            counter++;
          } else {
            result.push({
              content: getColumnGroupHeaderName(groupIds[i]),
              colSpan: counter,
            });
            counter = 1;
          }
        }
        return result;
      };

      return groupLevelIndexes.map(getLevelGroupIds).map(getColSpans);
    };

    const tableHead = [
      ...getTableColumnGroups(),
      visibleColumns.map((column) => {
        return { content: column.headerName ?? column.field };
      }),
    ];

    // Table Data Rows
    const getRowsData = (): GridRowId[] => {
      const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);

      const getSelectionRowIds = () => {
        const selectedRows = apiRef.current.getSelectedRows();

        if (selectedRows.size > 0) {
          const exportRowIds = getRowsToExport
            ? getRowsToExport({ apiRef })
            : [];

          return exportRowIds.length > 0
            ? exportRowIds
            : Array.from(selectedRows.keys());
        }

        return [];
      };

      const selectionRowIds = getSelectionRowIds();

      return selectionRowIds.length > 0
        ? filteredSortedRowIds.filter((rowId) =>
            selectionRowIds.includes(rowId),
          )
        : filteredSortedRowIds;
    };

    const tableBody = getRowsData().map((id) => {
      const rowData = Object.fromEntries(
        visibleColumns.map(({ field }) => {
          const { formattedValue } = apiRef.current.getCellParams(id, field);

          return [field, formattedValue];
        }),
      );

      return { rowId: id, ...rowData };
    });

    // Resolve Page size
    const resolvePageWidth = (defaultColumnWidth: number, minWidth: number) => {
      const defaultPageWidth = visibleColumns.length * defaultColumnWidth;

      const getOnlyNumberValues = (values: (number | undefined)[]) =>
        values.filter((width): width is number => typeof width === 'number');

      const getLargestValue = (values: number[], defaultValue: number) =>
        last(sortBy(values)) ?? defaultValue;

      const resolvedColumnWidths = visibleColumns.map(
        ({ width, minWidth, computedWidth }) => {
          return getLargestValue(
            getOnlyNumberValues([
              defaultColumnWidth,
              width,
              minWidth,
              computedWidth,
            ]),
            defaultColumnWidth,
          );
        },
      );

      const resolvedPageWidth = resolvedColumnWidths.reduce(
        (total: number, colWidth) => {
          const addWidth: number = colWidth ?? defaultColumnWidth;
          return total + addWidth;
        },
        0,
      );

      return getLargestValue(
        getOnlyNumberValues([minWidth, defaultPageWidth, resolvedPageWidth]),
        minWidth ?? defaultPageWidth,
      );
    };

    const resolvePageHeight = (minContentRowHeight: number) => {
      const buffer = 100;
      const tableHeadHeight = tableHead.length * (minContentRowHeight + 10);
      const tableContentHeight = tableBody.length * minContentRowHeight;

      return (
        documentDetailsSection.height +
        buffer +
        tableHeadHeight +
        tableContentHeight
      );
    };

    const pageSize = {
      width: resolvePageWidth(150, 900),
      height: resolvePageHeight(30),
    };

    // Construct PDF
    const doc = new jsPDF({
      orientation: pageSize.height < pageSize.width ? 'l' : 'p',
      unit: 'px',
      format: [pageSize.height, pageSize.width],
      hotfixes: ['px_scaling'],
    });

    // Include Document details
    doc.setTextColor(0, 0, 0);
    documentDetailsSection.rows.forEach((row) => {
      const { text, fontSize, rowOffset, color } = row;

      doc.setFontSize(fontSize);
      if (!!color) {
        const [ch1, ch2, ch3] = color;
        doc.setTextColor(ch1, ch2, ch3);
      }
      doc.text(text, pageMargins.horizontal, pageMargins.vertical + rowOffset);
    });

    // Include Solaforce Logo
    const logo = resolveLogoPosition(
      pageSize.width,
      imgSize.width,
      imgSize.height,
    );
    doc.addImage(img, 'PNG', logo.x, logo.y, logo.w, logo.h);

    // Include Timestamp
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(timestamp.fontSize);
    doc.text(
      moment().format('LLLL'),
      pageSize.width - pageMargins.horizontal,
      pageMargins.vertical + logo.h,
      {
        align: 'right',
      },
    );

    // Include table
    autoTable(doc, {
      columns,
      head: tableHead,
      body: tableBody,
      bodyStyles: {
        cellWidth: 'wrap',
      },
      columnStyles,
      startY: pageMargins.vertical + documentDetailsSection.height,
    });

    doc.save(`${fileName}.pdf`);
  };
